<script>
import lazyLoadComponent from '@router/lazyload-component'
import {
  genderFilter,
  orderStatusFilter,
  orderSourceFilter,
  orderPaymentMethodFilter,
} from '@utils/filter-value-to-text'
import formatDate from '@utils/format-date'

import pageMixin from '@src/mixins/pagination'
import { PortalTarget } from 'portal-vue'

export default {
  components: {
    PortalTarget,
    BaseStateLabel: () =>
      lazyLoadComponent(import('@components/BaseStateLabel')),
    OrderDetail: () =>
      lazyLoadComponent(import('@components/PrepaidOrderDetail')),
    OrderForm: () => import('@components/PrepaidOrderForm'),
  },
  filters: {
    genderFilter,
    orderStatusFilter,
    orderSourceFilter,
    orderPaymentMethodFilter,
    formatDate,
  },
  mixins: [pageMixin],
  props: {
    defaultValue: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      loadedOrders: [],
      tryingToGetOrder: false,
      drawerVisible: false,
      drawerOrderData: '',
      drawerMode: 'detail',
    }
  },
  computed: {
    enabledCreateOrder() {
      return (
        this.defaultValue.infinity === 1 ||
        (this.defaultValue.infinity === 0 &&
          this.defaultValue.inventory > this.defaultValue.qty)
      )
    },
  },
  watch: {
    currentPage: {
      handler: 'tryToFetchOrder',
      immediate: true,
    },
  },
  methods: {
    updateDrawerMode(value) {
      if (this.drawerMode !== value) this.drawerMode = value
    },
    calcRowClassName({ row }) {
      const orderStatus = row.status
      const orderUsedStatus = row.groupSize / row.usedSize

      if (orderStatus === 0) return 'order--canceled'
      if (orderUsedStatus === 1) return 'order--used'
    },
    async tryToGetOrder(row) {
      this.tryingToGetOrder = true
      this.drawerOrderData = await this.$store.dispatch(
        'prepaid/fetchOrderSingle',
        row.id
      )
      this.tryingToGetOrder = false
      this.updateDrawerMode('detail')
      this.drawerVisible = true
    },
    async tryToFetchOrder() {
      const { items, total } = await this.$store.dispatch(
        'prepaid/fetchOrders',
        Object.assign(
          {
            prepaidId: this.defaultValue.id,
            paymentMethod: 'All',
          },
          this.pageQuery
        )
      )

      this.loadedOrders = items
      this.$nextTick(() => {
        this.$refs.pagination.updateTotal(total)
      })
    },
    handleCreateOrder() {
      this.updateDrawerMode('add')
      this.drawerVisible = true
    },
    handleCloseDrawer() {
      this.drawerVisible = false
    },
    handleUpdateOrders() {
      this.$emit('update:defaultValue', {
        ...this.defaultValue,
        qty: this.defaultValue.qty + 1,
      })
      this.tryToFetchOrder()
      this.handleCloseDrawer()
    },
  },
}
</script>

<template>
  <div>
    <BasePageHeader title="此預付方案客戶購買紀錄">
      <template v-slot:right>
        <el-button
          :disabled="!enabledCreateOrder"
          type="primary"
          @click="handleCreateOrder"
        >代客購買此預付方案</el-button>
      </template>
    </BasePageHeader>
    <el-table
      v-loading.fullscreen.lock="tryingToGetOrder"
      element-loading-text="取得訂單資訊中"
      :data="loadedOrders"
      :default-sort="{ prop: 'date', order: 'descending' }"
      :row-class-name="calcRowClassName"
      @row-click="tryToGetOrder"
    >
      <el-table-column v-slot="{ row }" label="#訂單編號" sortable>
        <span>{{ `#${row.orderNo}` }}</span>
        <div>{{ row.created | formatDate('dateTime') }}</div>
      </el-table-column>
      <el-table-column v-slot="{ row }" label="狀態與來源" width="120" sortable>
        <BaseStateLabel
          :state="row.status | orderStatusFilter('color') "
          text
        >{{ row.status | orderStatusFilter('text') }}</BaseStateLabel>
        <br />
        <BaseStateLabel state="info" text>{{ row.source | orderSourceFilter }}</BaseStateLabel>
      </el-table-column>
      <el-table-column
        v-slot="{ row }"
        label="付款方式"
        width="100"
        sortable
      >{{ row.paymentMethod | orderPaymentMethodFilter }}</el-table-column>
      <el-table-column v-slot="{ row }" label="客戶資訊">
        <div>{{ row.fullName }} {{ row.gender | genderFilter }}</div>
        <div>{{ `${row.phoneCode} ${row.phoneNumber}` }}</div>
      </el-table-column>
      <el-table-column width="50">
        <el-tooltip content="查看訂單詳情" placement="top">
          <el-button type="text">
            <BaseIcon name="ellipsis-v" />
          </el-button>
        </el-tooltip>
      </el-table-column>
    </el-table>
    <BasePagination ref="pagination" :current-page.sync="currentPage" />
    <el-drawer :visible.sync="drawerVisible" append-to-body size="50%">
      <template v-if="drawerMode === 'detail'">
        <!-- header -->
        <PortalTarget name="drawer-header" slot="title"></PortalTarget>
        <OrderDetail
          ref="orderDetail"
          :default-value.sync="drawerOrderData"
          @update="tryToFetchOrder"
        />
      </template>
      <template v-else>
        <h4>{{ defaultValue.title }}</h4>
        <h5>{{ `${defaultValue.times}次／$${defaultValue.price}` }}</h5>
        <OrderForm :prepaid="defaultValue" @store="handleUpdateOrders" @cancel="handleCloseDrawer" />
      </template>
    </el-drawer>
  </div>
</template>
